import {Vector} from "ol/layer";
import {Feature} from "ol";
import {Fill, Icon, Stroke, Style} from "ol/style";
import {Geometry, Point} from "ol/geom";
import RenderFeature from "ol/render/Feature";
import {StyleFunction} from "ol/style/Style";
import {FeatureLike} from "ol/Feature";
import VectorSource from "ol/source/Vector";
import {resourceURL} from "../../utility/resourceURL";

enum ZIndex {
    Polygon = 10008,
    HighlightedPolygon = 10009,
    Pin = 10010,
    PinSelected = 10011,
    PinHovered = 10011,
}

const pinWidth = 37;
const pinHeight = 64;

const pinStyleCache: {[key: string]: Style} = {};
function pinStyleFromURL(src: string, opacity: number): Style {
    const k = `${src}@${opacity}`;

    if (!pinStyleCache[k]) {
        pinStyleCache[k] = new Style({
            image: new Icon({
                anchor: [0.5, 1.0],
                src,
                scale: 0.5,
                size: [pinWidth, pinHeight],
                opacity
            })
        });
    }

    return pinStyleCache[k];
}

const defaultPinURL = resourceURL('/themes/custom/images/pin-red.png');
const selectedPinURL = resourceURL('/themes/custom/images/pin-orange.png');
const savedPinURL = resourceURL('/themes/custom/images/pin-green.png');
const hoverPinURL = resourceURL('/themes/custom/images/pin-blue.png');


const hiddenStyle = new Style(<any> {
    renderer: () => {}
});

function deleting<T extends Geometry>(feature: Feature<T> | RenderFeature) {
    return feature.get('deleting');
}

function getPinStyle(feature:  (Feature<Geometry> | RenderFeature), resolution:  number): (Style | Style[]) {
    if (feature.get('hidden') || deleting(feature)) {
        return [hiddenStyle];
    }
    // const opacity = feature.get('saved') ? 0.82 : 0.7;
    let opacity = 0.75;
    let url = defaultPinURL;
    if (feature.get('hover')) {
        url = hoverPinURL;
    } else if (feature.get('selected')) {
        url = selectedPinURL;
        opacity = 0.9;
    } else if (feature.get('saved')) {
        url = savedPinURL;
    }

    // const url = feature.get('hover') ? hoverPinURL :
    //     (feature.get('selected') ? selectedPinURL: defaultPinURL);

    return [pinStyleFromURL(url, opacity)];
}

export const defaultDrawingLayerStyle = new Style({
    fill: new Fill({
        color: 'rgba(255, 255, 255, 0.6)'
    }),
    stroke: new Stroke({
        color: 'rgba(255, 67, 67, 0.7)',
        width: 3
    }),
    zIndex: ZIndex.Polygon
});

const highlightedDrawingLayerStyle = new Style({
    fill: new Fill({
        color: 'rgba(255, 255, 255, 0.8)'
    }),
    stroke: new Stroke({
        color: 'rgba(67, 67, 255, 0.9)',
        width: 3
    }),
    zIndex: ZIndex.HighlightedPolygon
});

export const defaultOlPolygonStyle = new Style({
    fill: new Fill({
        color: 'rgba(255,255,255,0.4)'
    }),
    stroke: new Stroke({
        color: '#3399CC',
        width: 1.25
    })
})

function getPolygonStyle(feature: Feature<Geometry>, resolution: number) {
    if (deleting(feature)) {
        return [hiddenStyle];
    }

    return feature.get('hover') ? highlightedDrawingLayerStyle : defaultDrawingLayerStyle;
}

export const hoverStyleFn = ((f: FeatureLike, resolution: number) => {
    return f.getGeometry() instanceof Point
        ? getPinStyle(f, resolution)
        : highlightedDrawingLayerStyle
});

export const selectedStyleFn = ((f: FeatureLike, resolution: number) => {
    return f.getGeometry() instanceof Point
        ? getPinStyle(f, resolution)
        : defaultDrawingLayerStyle
});

export const customLayerStyleFn: StyleFunction = function(feature : Feature<any>, resolution: number) : Style|Style[] {
    /*if (!feature || feature.get('hidden')) {
        return [hiddenStyle];
    }*/

    if (deleting(feature)) {
        return [hiddenStyle];
    }

    if (feature.getStyle()) {
        return feature.getStyle() as Style;
    }

    if (feature.getGeometry() instanceof Point) {
        return getPinStyle(feature, resolution);
    }

    if (feature.get('hover')) {
        return highlightedDrawingLayerStyle;
    }

    if (feature.get('selected')) {
        return defaultDrawingLayerStyle;
    }

    return defaultOlPolygonStyle;
};

export class FeaturesLayer extends Vector<VectorSource<Geometry>> {
    constructor(opt_options) {
        opt_options.style = function(feature : Feature<Geometry>, resolution: number) : Style|Style[] {
            if (!feature || feature.get('hidden')) {
                return [hiddenStyle];
            }
            if (feature.getGeometry() instanceof Point) {
                return getPinStyle(feature, resolution);
            }

            return getPolygonStyle(feature, resolution);
        };

        super(opt_options);
    }
}
